"use client";

import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { cn } from "@/utils/cn";

type AutoSliderProps = PropsWithChildren & {
  className?: string;
  isPause: boolean;
};

export const AutoSlider: FC<AutoSliderProps> = ({
  className,
  children,
  isPause,
}) => {
  const [animating, setAnimating] = useState(false);
  const [slides, setSlides] = useState(1);

  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const lastWidth = useRef(0);

  const resetAnimation = useCallback(() => {
    setAnimating(false);

    if (isPause) {
      return;
    }

    setTimeout(() => {
      setAnimating(true);
    }, 100);
  }, [isPause]);

  const setupSlides = useCallback(() => {
    const currentWidth = window.innerWidth;
    if (currentWidth === lastWidth.current) {
      return;
    }

    if (!innerRef?.current || !outerRef?.current) {
      return;
    }

    const { width: innerWidth } = innerRef.current.getBoundingClientRect();
    const { width: parentWidth } = outerRef.current.getBoundingClientRect();
    const slideWidth = innerWidth / innerRef.current.children.length;

    if (innerWidth < parentWidth + slideWidth) {
      setSlides((prev) => prev + Math.ceil(parentWidth / innerWidth));
    }

    resetAnimation();
    lastWidth.current = currentWidth;
  }, [resetAnimation]);

  useEffect(() => {
    if (isPause) {
      setAnimating(false);
      return;
    }

    setAnimating(true);
  }, [isPause]);

  useEffect(() => {
    setupSlides();
  }, [setupSlides]);

  useEffect(() => {
    window.addEventListener("resize", setupSlides);

    return () => {
      window.removeEventListener("resize", setupSlides);
    };
  }, [setupSlides]);

  return (
    <div
      className={cn(
        "pointer-events-none z-2 flex w-full justify-start overflow-hidden py-4 select-none",
        "xl:py-12",
        className,
      )}
      ref={outerRef}
    >
      <div ref={innerRef} className="flex shrink-0">
        {[...Array(slides)].map((_, index) => (
          <div
            key={index}
            className={cn(
              "flex shrink-0 animate-slide-to-left items-center gap-4 will-change-transform",
              animating ? "animation-run" : "animation-pause",
            )}
          >
            {children}
          </div>
        ))}
      </div>
    </div>
  );
};
